<template>
  <table
    v-if="table"
    class="size-chart-table"
  >
    <thead v-if="table.header_row?.cells.length">
      <tr>
        <th
          v-for="(cell, cellIndex) in table.header_row?.cells"
          :key="`thead-${cellIndex}`"
          v-html="cell.label"
        />
      </tr>
    </thead>
    <tbody>
      <template
        v-for="(row, bodyRowIndex) in table.body_rows"
        :key="`tbody-${bodyRowIndex}`"
      >
        <tr
          v-for="(tr, trIndex) in calculateRowCount(row)"
          :key="`trow-${trIndex}`"
        >
          <td
            v-for="(cell, cellIndex) in sliceRowCells(row, trIndex)"
            :key="`table-data-${cellIndex}`"
            :rowspan="calculateRowSpan(trIndex, cellIndex, row)"
            :class="{ 'font-medium': shouldApplyFont(trIndex, cellIndex) }"
            v-html="formatCell(cell.label)"
          />
        </tr>
      </template>
    </tbody>
  </table>
</template>
<script>

/**
 * Component: Size Chart Table
 * ------------------------------------------------------------------------------
 * - Size Chart Table component.
 * @param {String} table - Formatted object from metaobject entries.
 * @param {Array}  table.rows -  Array of the all table rows.
 * @param {Array}  table.header_row -  Array of rows with table cells, which belong to table header.
 * @param {Array}  table.body_rows - Array of rows with table cells, which belong to table body.
 * @param {Number} table.column_count - Total quantity of table columns.
 * @param {String} table.region - Current selected region (to which the table is assigned).
 * @param {String} table.unit_type - Current selected unit type (to which the table is assigned).
 *
 */

export default {
  name: 'SizeChartTable',

  props: {
    table: {
      type: Object,
      default: null,
    },
  },

  methods: {

    /**
     * Calculate the number of `<tr>` needed for a given row.
     * @param {object} row - The formatted row object.
     *
     * @returns {number} - The number of table rows needed.
     */
    calculateRowCount(row) {
      return Math.ceil(
        (row?.cells?.length - 1) / (this.table?.column_count - 1 || 1),
      )
    },

    /**
     * Calculate the rowspan value for a cell based on its position and row characteristics.
     * @param {number} trIndex - Current table row index.
     * @param {number} cellIndex - Current cell index.
     * @param {object} row - The formatted row object.
     *
     * @returns {number} - The rowspan value for the cell.
     */
    calculateRowSpan(trIndex, cellIndex, row) {
      if (
        trIndex === 0 &&
        cellIndex === 0 &&
        row.cells.length > this.table.column_count
      ) {
        return this.calculateRowCount(row)
      }

      return 1
    },

    /**
     * Format the cells array for a specific row.
     * @param {object} row - The formatted row object.
     * @param {number} trIndex - Current table row index.
     *
     * @returns {Array} - The formatted array of cells for the row.
     */
    sliceRowCells(row, trIndex) {
      const { cells } = row
      const startIndex = trIndex * this.table.column_count
      const endIndex = (trIndex + 1) * this.table.column_count

      return cells.slice(startIndex, endIndex)
    },

    /**
     * Formats a cell label:
     * - replace spaces before numbers with a line break `<br>`
     * - if no string is passed, then return a space `' '`
     * - if cell contains word 'empty' - return a space `' '` as well
     * @param {string} string - The input string to format.
     *
     * @returns {string} The formatted cell label.
     * @example
     * input:  formatCell('Regular 74')
     * output: `Regular <br> 74`
     */
    formatCell(string) {
      if (!string || string.toLowerCase().includes('empty')) {
        return ' '
      }

      return string.trim().replace(/(\s)(?=\d)/g, '<br> ')
    },

    /**
     * Check if the cell should have an updated font-weight.
     * - additional check that current table does not have any header_row cells,
     * so that this rule applies only to additional (mainly related to
     * `fit inside leg`) tables.
     * @param {number} trIndex - Current table row index.
     * @param {number} cellIndex - Current cell index.
     *
     * @returns {boolean} - True if the custom font etc. should be applied.
     */
    shouldApplyFont(trIndex, cellIndex) {
      return (
        trIndex === 0 &&
        cellIndex === 0 &&
        !this.table.header_row?.cells?.length
      )
    },
  },
}
</script>
